import { useLayoutEffect, type MutableRefObject, type RefObject } from "react";
import type { ChatMessage } from "@/types/chat.type";
import { motionReduced } from "@/lib/motion";

/** Where your words left the composer, so the bubble can fly from there. */
export type SendFlight = { left: number; top: number; text: string; at: number };

const FLIGHT_MS = 320;
const FLIGHT_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
/** A flight not matched by then was queued behind a file; it just appears. */
const FLIGHT_TTL_MS = 4000;
/** The bubble's padding: its words start this far inside it. */
const BUBBLE_INSET = { left: 14, top: 10 };

/**
 * The send: your words leave the composer and land as your bubble. The bubble
 * is laid out in its place, then starts where the words sat in the field and
 * floats up into it. Measured after the log has scrolled to its end, so the
 * flight lands where the bubble will actually sit.
 *
 * The composer records `flightRef` as it sends; the first of your messages
 * whose words match takes it. `isOwn` picks your messages out of a thread
 * several people write in; `textOf` strips what the bubble does not show.
 */
export function useSendFlight({
  flightRef,
  scrollRef,
  messages,
  isOwn = (message) => message.role === "user",
  textOf = (message) => message.content,
}: {
  flightRef: MutableRefObject<SendFlight | null> | undefined;
  scrollRef: RefObject<HTMLDivElement | null>;
  messages: ChatMessage[];
  isOwn?: (message: ChatMessage) => boolean;
  textOf?: (message: ChatMessage) => string;
}) {
  useLayoutEffect(() => {
    const flight = flightRef?.current;
    const log = scrollRef.current;
    if (!flightRef || !flight || !log) return;
    if (performance.now() - flight.at > FLIGHT_TTL_MS) {
      flightRef.current = null;
      return;
    }
    const mine = [...messages].reverse().find(isOwn);
    if (!mine || textOf(mine).trim() !== flight.text.trim()) return;
    flightRef.current = null;
    log.scrollTop = log.scrollHeight;
    const bubble = log.querySelector<HTMLElement>(`[data-msg-id="${CSS.escape(mine.id)}"]`);
    if (!bubble) return;
    if (motionReduced()) {
      bubble.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: "ease-out" });
      return;
    }
    const to = bubble.getBoundingClientRect();
    const dx = flight.left - (to.left + BUBBLE_INSET.left);
    const dy = flight.top - (to.top + BUBBLE_INSET.top);
    bubble.animate(
      [
        { transform: `translate(${dx}px, ${dy}px)`, opacity: 0.7 },
        { transform: "translate(0, 0)", opacity: 1 },
      ],
      { duration: FLIGHT_MS, easing: FLIGHT_EASE },
    );
    // Reads the latest predicates; only the messages decide when to look again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, flightRef, scrollRef]);
}
